import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import GeneralAuditionsPanel from '../components/GeneralAuditionsPanel';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { markOpenAuditionsViewed } from '../utils/openAuditions';
import { getPublicAuditionShareUrl, shareLink } from '../utils/share';
import './OpenAuditions.css';

interface ConfirmState {
  open: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

type AuditionsTab = 'browse' | 'host';

type AuditionMedia = {
  id: number;
  url: string;
  media_type: 'photo' | 'video' | string;
};

type AuditionRecord = {
  id: number;
  story_id: number | null;
  story_title?: string | null;
  owner_name?: string;
  owner_email?: string;
  storyline?: string;
  character_line?: string;
  description?: string;
  created_at?: string;
  media?: AuditionMedia[];
  hasResponded?: boolean;
};

const isGeneralAudition = (audition: AuditionRecord | null | undefined) => !audition?.story_id;

const getAuditionTitle = (audition: AuditionRecord) =>
  audition.story_title || audition.character_line || `Audition #${audition.id}`;

const getAuditionScopeLabel = (audition: AuditionRecord | null | undefined) =>
  isGeneralAudition(audition) ? 'General' : 'Story Based';

const getAuditionScopeClassName = (audition: AuditionRecord | null | undefined) =>
  isGeneralAudition(audition) ? 'audition-scope-general' : 'audition-scope-story';

const getAuditionPreview = (audition: AuditionRecord) =>
  isGeneralAudition(audition)
    ? audition.description || audition.character_line || 'General audition'
    : audition.character_line || audition.storyline || audition.description || 'Open audition';

const sortBrowseAuditions = (auditions: AuditionRecord[]) =>
  [...auditions].sort((a, b) => {
    if (Boolean(a.hasResponded) !== Boolean(b.hasResponded)) {
      return a.hasResponded ? 1 : -1;
    }

    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

const OpenAuditionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const requestedAuditionId = useMemo(() => {
    const rawValue = searchParams.get('auditionId');
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }, [searchParams]);
  const initialTab: AuditionsTab = requestedAuditionId != null
    ? 'browse'
    : searchParams.get('tab') === 'host'
      ? 'host'
      : 'browse';
  const activeTab: AuditionsTab = requestedAuditionId != null
    ? 'browse'
    : searchParams.get('tab') === 'host'
      ? 'host'
      : 'browse';

  const [auditions, setAuditions] = useState<AuditionRecord[]>([]);
  const [loading, setLoading] = useState(initialTab === 'browse');
  const [selectedAudition, setSelectedAudition] = useState<AuditionRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasLoadedBrowse, setHasLoadedBrowse] = useState(false);
  const [hasVisitedHost, setHasVisitedHost] = useState(initialTab === 'host');
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [mediaModal, setMediaModal] = useState<{
    isOpen: boolean;
    url: string;
    type: 'video' | 'photo';
    mediaId?: number;
  }>({ isOpen: false, url: '', type: 'video' });
  const refreshingMediaRef = useRef<Set<number>>(new Set());
  const canSubmitResponse = isAuthenticated && Boolean(user?.profileCompleted);

  const switchTab = useCallback((tab: AuditionsTab) => {
    setSelectedAudition(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('auditionId');
    if (tab === 'host') {
      nextParams.set('tab', 'host');
    } else {
      nextParams.delete('tab');
    }
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const loadAuditionDetail = useCallback(async (
    auditionId: number,
    baseAudition?: AuditionRecord | null,
  ) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/public-auditions/${auditionId}`);
      const nextAudition: AuditionRecord = {
        ...(baseAudition || {}),
        ...(response.data?.audition || {}),
        hasResponded:
          response.data?.audition?.hasResponded ??
          baseAudition?.hasResponded ??
          selectedAudition?.hasResponded,
        media: Array.isArray(response.data?.media) ? response.data.media : [],
      };

      setSelectedAudition((current) => (current?.id === auditionId || !current ? nextAudition : current));
    } catch (loadError) {
      console.error('Failed to load audition detail:', loadError);
      setError('Failed to load audition');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedAudition?.hasResponded]);

  const refreshSelectedAuditionMediaUrl = useCallback(async (mediaId?: number) => {
    if (!mediaId || !selectedAudition?.id || refreshingMediaRef.current.has(mediaId)) return null;
    refreshingMediaRef.current.add(mediaId);
    try {
      const response = await api.get(`/public-auditions/${selectedAudition.id}/media`);
      const nextMedia: AuditionMedia[] = response.data?.media || [];
      const freshItem = nextMedia.find((item) => Number(item.id) === Number(mediaId));
      const freshUrl = freshItem?.url || null;
      setSelectedAudition((current) => (current ? { ...current, media: nextMedia } : current));
      if (freshUrl) {
        setMediaModal((current) => (
          current.mediaId === mediaId ? { ...current, url: freshUrl } : current
        ));
      }
      return freshUrl;
    } catch (refreshError) {
      console.error('Refresh open audition media URL failed:', refreshError);
      return null;
    } finally {
      refreshingMediaRef.current.delete(mediaId);
    }
  }, [selectedAudition?.id]);

  const loadAuditions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/public-auditions?includeMedia=false');
      const sortedAuditions = sortBrowseAuditions((response.data.auditions || []) as AuditionRecord[]);
      setAuditions(sortedAuditions);
      markOpenAuditionsViewed(sortedAuditions);

      if (selectedAudition) {
        const fresh = sortedAuditions.find((audition) => audition.id === selectedAudition.id) || null;
        if (!fresh) {
          setSelectedAudition(null);
        } else {
          setSelectedAudition((current) =>
            current?.id === fresh.id
              ? {
                  ...current,
                  ...fresh,
                  media: current.media || [],
                }
              : current,
          );
          void loadAuditionDetail(fresh.id, fresh);
        }
      }
    } catch (loadError) {
      console.error('Failed to load auditions:', loadError);
      setError('Failed to load auditions');
    } finally {
      setLoading(false);
    }
  }, [loadAuditionDetail, selectedAudition]);

  useEffect(() => {
    if (activeTab === 'host' && !hasVisitedHost) {
      setHasVisitedHost(true);
    }

    if (activeTab === 'browse' && !hasLoadedBrowse) {
      setHasLoadedBrowse(true);
      void loadAuditions();
    }
  }, [activeTab, hasLoadedBrowse, hasVisitedHost, loadAuditions]);

  useEffect(() => {
    if (activeTab !== 'browse' || requestedAuditionId == null) {
      return;
    }

    const previewAudition = auditions.find((audition) => audition.id === requestedAuditionId) || null;

    if (previewAudition) {
      setSelectedAudition((current) =>
        current?.id === previewAudition.id
          ? {
              ...current,
              ...previewAudition,
              media: current.media || [],
            }
          : {
              ...previewAudition,
              media: [],
            },
      );
    }

    void loadAuditionDetail(requestedAuditionId, previewAudition);
  }, [activeTab, auditions, loadAuditionDetail, requestedAuditionId]);

  const showConfirm = (nextMessage: string): Promise<boolean> =>
    new Promise((resolve) => {
      setConfirm({
        open: true,
        message: nextMessage,
        onConfirm: () => {
          setConfirm((current) => ({ ...current, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirm((current) => ({ ...current, open: false }));
          resolve(false);
        },
      });
    });

  const doUpload = async (file: File, auditionId: number) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/public-auditions/${auditionId}/respond`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage('Response submitted successfully!');
      setTimeout(() => setMessage(''), 3000);
      await loadAuditions();
      setSelectedAudition((current) =>
        current?.id === auditionId ? { ...current, hasResponded: true } : current,
      );
    } catch (uploadError: any) {
      setError(uploadError.response?.data?.message || 'Failed to submit response');
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleResponseUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    auditionId: number,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      setError('File size exceeds 100MB limit');
      setTimeout(() => setError(''), 3000);
      event.target.value = '';
      return;
    }

    const confirmed = await showConfirm(
      `Submit "${file.name}" as your audition response? This cannot be changed once submitted.`,
    );

    if (!confirmed) {
      event.target.value = '';
      return;
    }

    await doUpload(file, auditionId);
    event.target.value = '';
  };

  const filteredAuditions = useMemo(() => {
    if (!searchQuery.trim()) return auditions;
    const query = searchQuery.toLowerCase();

    return auditions.filter((audition) =>
      [
        audition.owner_name,
        audition.owner_email,
        audition.character_line,
        audition.story_title,
        audition.storyline,
        audition.description,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [auditions, searchQuery]);

  const openAuditionDetail = useCallback((audition: AuditionRecord) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('tab');
    nextParams.set('auditionId', String(audition.id));
    setSearchParams(nextParams);
    setSelectedAudition((current) =>
      current?.id === audition.id
        ? {
            ...current,
            ...audition,
          }
        : {
            ...audition,
            media: [],
          },
    );
    void loadAuditionDetail(audition.id, audition);
  }, [loadAuditionDetail, searchParams, setSearchParams]);

  const closeAuditionDetail = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/content');
      return;
    }

    setSelectedAudition(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('auditionId');
    setSearchParams(nextParams, { replace: true });
  }, [isAuthenticated, navigate, searchParams, setSearchParams]);

  const handleShareAudition = useCallback(async (audition: AuditionRecord) => {
    try {
      const outcome = await shareLink(
        getPublicAuditionShareUrl(audition.id),
        `${getAuditionTitle(audition)} on FilMini`,
      );
      if (outcome === 'copied') {
        setMessage('Audition link copied to clipboard.');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (shareError) {
      console.error('Failed to share audition:', shareError);
      setError('Failed to share audition link');
      setTimeout(() => setError(''), 3000);
    }
  }, []);

  const showBrowseLoading = activeTab === 'browse' && (!hasLoadedBrowse || loading);

  return (
    <div className="open-auditions-container">
      {confirm.open ? (
        <div className="oa-confirm-overlay">
          <div className="oa-confirm-box">
            <p className="oa-confirm-message">{confirm.message}</p>
            <div className="oa-confirm-actions">
              <button className="oa-confirm-btn-ok" onClick={confirm.onConfirm}>
                Submit
              </button>
              <button className="oa-confirm-btn-cancel" onClick={confirm.onCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="open-auditions-header">
        <button onClick={() => navigate('/content')} className="back-btn">
          Back to Home
        </button>
        <h1>Public Auditions</h1>
      </div>

      {message ? <div className="success-message">{message}</div> : null}
      {error ? <div className="error-message">{error}</div> : null}

      <div className="open-auditions-intro">
        <p>
          Browse public casting calls or switch to the Host tab to publish and manage your own general auditions.
        </p>
      </div>

      <div className="auditions-main-tabs">
        <button
          className={`auditions-main-tab ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => switchTab('browse')}
        >
          Browse Auditions
        </button>
        <button
          className={`auditions-main-tab ${activeTab === 'host' ? 'active' : ''}`}
          onClick={() => switchTab('host')}
        >
          Host Auditions
        </button>
      </div>

      <div className={`auditions-tab-panel auditions-tab-panel--browse ${activeTab === 'browse' ? 'is-active' : 'is-hidden'}`}>
        {showBrowseLoading ? (
          <div className="loading">Loading auditions...</div>
        ) : auditions.length === 0 ? (
          <div className="no-auditions">
            <p>No open auditions available right now.</p>
          </div>
        ) : selectedAudition ? (
          <div className="audition-detail">
            <button onClick={closeAuditionDetail} className="back-btn">
              Back to List
            </button>

            <div
              className={`audition-detail-card ${getAuditionScopeClassName(selectedAudition)} ${isGeneralAudition(selectedAudition) ? 'audition-detail-card--general' : 'audition-detail-card--story'} ${selectedAudition.hasResponded ? 'audition-detail-card--applied' : ''}`}
            >
              <span className={`audition-scope-badge ${getAuditionScopeClassName(selectedAudition)}`}>
                {getAuditionScopeLabel(selectedAudition)}
              </span>
              <h2>{getAuditionTitle(selectedAudition)}</h2>
              <p className="audition-owner">by {selectedAudition.owner_name}</p>
              <div className="audition-detail-actions">
                <button
                  type="button"
                  className="audition-share-btn audition-share-btn--detail"
                  onClick={() => void handleShareAudition(selectedAudition)}
                >
                  Share Audition
                </button>
              </div>
              {detailLoading ? <p className="search-results-info">Refreshing audition details...</p> : null}

              {isGeneralAudition(selectedAudition) ? (
                <div className="audition-detail-section">
                  <h3>Role</h3>
                  <p>{selectedAudition.character_line || 'No role shared yet.'}</p>
                </div>
              ) : (
                <>
                  <div className="audition-detail-section">
                    <h3>Story Line</h3>
                    <p>{selectedAudition.storyline}</p>
                  </div>

                  <div className="audition-detail-section">
                    <h3>Character Line</h3>
                    <p>{selectedAudition.character_line}</p>
                  </div>
                </>
              )}

              <div className="audition-detail-section">
                <h3>Description</h3>
                <p>{selectedAudition.description}</p>
              </div>

              {selectedAudition.media?.length ? (
                <div className="audition-detail-section">
                  <h3>Media</h3>
                  <div className="audition-media-grid">
                    {selectedAudition.media.map((media) => (
                      <div
                        key={media.id}
                        className="audition-media-item"
                        onClick={() =>
                          setMediaModal({
                            isOpen: true,
                            url: media.url,
                            type: media.media_type === 'photo' ? 'photo' : 'video',
                            mediaId: media.id,
                          })
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {media.media_type === 'photo' ? (
                          <img
                            src={media.url}
                            alt=""
                            onError={() => { void refreshSelectedAuditionMediaUrl(media.id); }}
                          />
                        ) : (
                          <video
                            muted
                            src={media.url}
                            onError={() => { void refreshSelectedAuditionMediaUrl(media.id); }}
                          >
                          </video>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="audition-response-section">
                {selectedAudition.hasResponded ? (
                  <div className="already-responded">
                    You have already submitted a response for this audition.
                  </div>
                ) : authLoading ? (
                  <>
                    <h3>Submit Your Audition</h3>
                    <p>Checking your account before enabling submissions...</p>
                  </>
                ) : !isAuthenticated ? (
                  <>
                    <h3>Submit Your Audition</h3>
                    <p>Log in to upload a photo or video as your audition response.</p>
                    <button
                      type="button"
                      className="response-upload-btn response-upload-btn--button"
                      onClick={() => navigate('/login')}
                    >
                      Login to Submit
                    </button>
                  </>
                ) : !canSubmitResponse ? (
                  <>
                    <h3>Submit Your Audition</h3>
                    <p>Complete your profile before submitting a response.</p>
                    <button
                      type="button"
                      className="response-upload-btn response-upload-btn--button"
                      onClick={() => navigate('/complete-profile')}
                    >
                      Complete Profile to Submit
                    </button>
                  </>
                ) : (
                  <>
                    <h3>Submit Your Audition</h3>
                    <p>Upload a photo, video, or PDF as your audition response.</p>
                    <label className="response-upload-btn">
                      {uploading ? 'Uploading...' : 'Upload Photo / Video / PDF'}
                      <input
                        type="file"
                        accept="image/*,video/*,application/pdf,.pdf"
                        onChange={(event) => void handleResponseUpload(event, selectedAudition.id)}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="auditions-list">
            <div className="search-filter-section">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="search-input-auditions"
              />
              {searchQuery ? (
                <div className="search-results-info">
                  {filteredAuditions.length} result{filteredAuditions.length !== 1 ? 's' : ''} found
                </div>
              ) : null}
            </div>

            {filteredAuditions.length === 0 ? (
              <div className="no-auditions">
                <p>No auditions found matching "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    marginTop: 10,
                    padding: '8px 16px',
                    background: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Clear Search
                </button>
              </div>
            ) : (
              filteredAuditions.map((audition) => {
                const preview = getAuditionPreview(audition);
                return (
                  <div
                    key={audition.id}
                    className={`audition-list-card ${getAuditionScopeClassName(audition)} ${isGeneralAudition(audition) ? 'audition-list-card--general' : 'audition-list-card--story'} ${audition.hasResponded ? 'audition-list-card--applied' : ''}`}
                    onClick={() => openAuditionDetail(audition)}
                  >
                    <div className="audition-list-info">
                      <h3>{getAuditionTitle(audition)}</h3>
                      <p className="audition-owner">by {audition.owner_name}</p>
                      <p className="audition-preview">
                        {preview.slice(0, 100)}
                        {preview.length > 100 ? '...' : ''}
                      </p>
                    </div>
                    <div className="audition-list-meta">
                      <span className={`audition-scope-badge ${getAuditionScopeClassName(audition)}`}>
                        {getAuditionScopeLabel(audition)}
                      </span>
                      {audition.hasResponded ? <span className="responded-badge">Applied</span> : null}
                      <span className="view-btn">View</span>
                      <button
                        type="button"
                        className="audition-share-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleShareAudition(audition);
                        }}
                      >
                        Share Audition
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className={`auditions-tab-panel auditions-tab-panel--host ${activeTab === 'host' ? 'is-active' : 'is-hidden'}`}>
        {authLoading ? (
          <div className="auditions-access-prompt">
            <p>Checking your account...</p>
          </div>
        ) : !isAuthenticated ? (
          <div className="auditions-access-prompt">
            <p>Log in to host auditions, manage submissions, and publish casting calls.</p>
            <button
              type="button"
              className="audition-share-btn audition-share-btn--detail"
              onClick={() => navigate('/login')}
            >
              Login to Host
            </button>
          </div>
        ) : !user?.profileCompleted ? (
          <div className="auditions-access-prompt">
            <p>Complete your profile before hosting and managing auditions.</p>
            <button
              type="button"
              className="audition-share-btn audition-share-btn--detail"
              onClick={() => navigate('/complete-profile')}
            >
              Complete Profile
            </button>
          </div>
        ) : hasVisitedHost ? <GeneralAuditionsPanel onBrowseAuditions={() => switchTab('browse')} /> : null}
      </div>

      {mediaModal.isOpen ? (
        <div
          className="media-modal-overlay"
          onClick={() => setMediaModal({ isOpen: false, url: '', type: 'video' })}
        >
          <div className="media-modal-content" onClick={(event) => event.stopPropagation()}>
            <button
              onClick={() => setMediaModal({ isOpen: false, url: '', type: 'video' })}
              className="modal-close-large"
            >
              Close
            </button>
            {mediaModal.type === 'video' ? (
              <video
                controls
                autoPlay
                className="modal-media"
                src={mediaModal.url}
                onError={() => { void refreshSelectedAuditionMediaUrl(mediaModal.mediaId); }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={mediaModal.url}
                alt=""
                className="modal-media"
                onError={() => { void refreshSelectedAuditionMediaUrl(mediaModal.mediaId); }}
              />
            )}
            <a
              href={mediaModal.url}
              download
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '12px',
                color: '#fff',
                background: '#222',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Download
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OpenAuditionsPage;
